import React, { PureComponent, createRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';

import AliveChatItem from 'app/components/organisms/Chat/AliveChatItem';
import ChatFooter from 'app/components/organisms/Chat/ChatFooter';
import ChatFileUploadModal from 'app/components/organisms/Chat/ChatFileUploadModal';
import DropzoneWrapper from 'app/components/molecules/Dropzone/DropzoneWrapper';
import ReadByDialog from 'app/components/organisms/Chat/ReadByDialog';
import Loader from 'app/components/atoms/Loader/Loader';
import { sendThreadMessage, uploadThreadFile } from 'store/actions/chat/chatThreadActions';
import { updateChatMessage } from 'store/actions/chat/chatActions';
import { focusChatInput, setChatInputCursorToLast } from 'app/utils/chat/chatUtils';
import { removeSpecialCharacters } from 'app/utils/string/string-utils';
import { bind } from 'app/utils/decorators/decoratorUtils';

// Thread-local dropzone (the main chat's dropzone deliberately excludes the thread, else
// drops would upload to the main room without tmid). Drops route to uploadFiles → the
// same "Upload file?" dialog as the paperclip. Mirrors main chat: dropzone is a height
// wrapper, the real flex column lives inside it.
const ViewWrapper = styled(DropzoneWrapper)`
    flex: 1;
    min-height: 0;
`;

const ColumnWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
`;

// Single scroll area holding the parent message + separator + replies, so the
// pinned parent scrolls together with the thread (RC behaviour). Horizontal inset
// comes from PanelWrapper's padding; the AvatarWrap 8px main-chat gutter is removed
// so avatars align with the header divider and composer.
const ScrollArea = styled.div`
    flex: 1;
    overflow-y: auto;
    & [class*='AliveChatItem__AvatarWrap'] {
        margin-left: 0;
    }
    /* The scroll area clips overflow (overflow-y:auto forces overflow-x:auto), so the
       avatar name tooltip's shared up-left placement (x=10/y=-50) gets cut at the panel's
       left/top edge. Open it to the right of the avatar — into the message content where
       there's always room — so it can't be clipped. */
    & [class*='AliveChatItem__AvatarWrap'] [alt]::after {
        top: 50%;
        left: 100%;
        transform: translateY(-50%);
        margin-left: 6px;
    }
`;

const ParentWrapper = styled.div`
    padding: 24px 0 16px;
    border-bottom: 1px solid ${({ theme }) => theme.material.colors.background.divider};
    /* drop the main-chat 24px bottom margin so the separator centers between the
       parent and the first reply (equal 16px gap above/below) */
    & [class*='AliveChatItem__MessageItemStyle'] {
        margin-bottom: 0;
    }
`;

const RepliesWrapper = styled.div`
    padding: 16px 0 8px;
`;

const FooterWrapper = styled.div`
    padding: 0;
`;

const noop = () => {};

class ThreadView extends PureComponent {
    static propTypes = {
        rid: PropTypes.string.isRequired,
        readOnly: PropTypes.bool,
        tmid: PropTypes.string,
        parentMessage: PropTypes.object,
        messages: PropTypes.array,
        isLoadingMessages: PropTypes.bool,
        userId: PropTypes.string,
        isMobile: PropTypes.bool,
        sendThreadMessage: PropTypes.func.isRequired,
        uploadThreadFile: PropTypes.func.isRequired,
        updateChatMessage: PropTypes.func.isRequired,
    };

    inputRef = createRef();
    repliesRef = createRef();
    state = { inputValue: '', inputPreviousValue: '', editActive: false, editMsgId: '', receiptsMessage: null, files: [], isUploading: false };

    componentDidMount() {
        focusChatInput(this.inputRef);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.messages !== this.props.messages && this.repliesRef.current) {
            this.repliesRef.current.scrollTop = this.repliesRef.current.scrollHeight;
        }
    }

    @bind
    onInputChange(event) {
        const { value } = event.target;
        if (!this.state.editActive) {
            this.setState({ inputValue: value, inputPreviousValue: value });
        } else {
            this.setState({ inputValue: value });
        }
    }

    // Edit a thread reply inline in the thread composer — never the main chat.
    // updateChatMessage hits RC chat.update (roomId + msgId only); the tmid is
    // preserved server-side and the edited reply re-renders via the WS thread gate.
    @bind
    onEdit(continueEditing, message = {}) {
        const { inputPreviousValue } = this.state;
        if (!continueEditing) {
            this.setState({ editActive: false, editMsgId: '', inputValue: inputPreviousValue || '' });
            return;
        }
        this.setState(
            { editActive: true, editMsgId: message.id, inputValue: message.text },
            () => setChatInputCursorToLast(this.inputRef)
        );
    }

    @bind
    async sendMessage() {
        const { rid, tmid, sendThreadMessage, updateChatMessage } = this.props;
        const { inputValue, inputPreviousValue, editActive, editMsgId } = this.state;
        if (!inputValue) return;
        if (editActive) {
            updateChatMessage({ _id: editMsgId, rid, msg: inputValue });
            this.setState({ editActive: false, editMsgId: '', inputValue: inputPreviousValue || '', inputPreviousValue: inputPreviousValue || '' });
            focusChatInput(this.inputRef);
            return;
        }
        this.setState({ inputValue: '', inputPreviousValue: '' });
        await sendThreadMessage(rid, tmid, inputValue);
        focusChatInput(this.inputRef);
    }

    @bind
    onInputKeyPress(event) {
        if (this.props.isMobile) return;
        if (event.key === 'Escape' && this.state.editActive) {
            this.onEdit(false);
            return;
        }
        if (event.key === 'Enter' && !event.shiftKey) {
            event.stopPropagation();
            event.preventDefault();
            this.sendMessage();
        }
    }

    @bind
    onShowReceipts(message) {
        this.setState({ receiptsMessage: message });
    }

    @bind
    onCloseReceipts() {
        this.setState({ receiptsMessage: null });
    }

    // Queue picked files into the "Upload file?" preview/rename dialog (ChatFileUploadModal),
    // matching the main chat — instead of uploading raw immediately. The sanitized filename
    // (removeSpecialCharacters) keeps the preview/download URL valid for names with special
    // characters (e.g. "1313)(&&^%#@..." → "131…"); raw names broke the RC file URL.
    @bind
    uploadFiles(files) {
        const fileArray = files instanceof File ? [files] : Array.isArray(files) ? files : Object.values(files || {});
        this.setState(prevState => ({
            files: [...prevState.files, ...fileArray.map(file => new File([file], removeSpecialCharacters(file.name), { type: file.type }))],
        }));
    }

    // Confirm from the dialog → upload the (renamed) file with its description to the thread.
    @bind
    async uploadFile({ file, filename, description }) {
        const { rid, tmid, uploadThreadFile } = this.props;
        this.setState({ isUploading: true });
        try {
            await uploadThreadFile(rid, tmid, file, removeSpecialCharacters(filename), description);
        } finally {
            this.setState(prevState => ({ files: prevState.files.slice(1), isUploading: false }));
        }
    }

    @bind
    closeFileForm() {
        this.setState({ files: [], isUploading: false });
    }

    render() {
        const { rid, parentMessage, messages, isLoadingMessages, readOnly, userId, isMobile } = this.props;
        const { inputValue, inputPreviousValue, editActive, editMsgId, receiptsMessage, files, isUploading } = this.state;
        return (
            <ViewWrapper onDropAccepted={readOnly ? undefined : this.uploadFiles} onDropRejected={readOnly ? undefined : this.uploadFiles}>
                <ColumnWrapper>
                    <ScrollArea ref={this.repliesRef}>
                        {parentMessage && (
                            <ParentWrapper>
                                <AliveChatItem
                                    message={parentMessage}
                                    rid={rid}
                                    userId={userId}
                                    openImagePreview={noop}
                                    onEdit={this.onEdit}
                                    onShowReceipts={this.onShowReceipts}
                                    editActive={parentMessage.id === editMsgId && editActive}
                                    isReadOnly={readOnly}
                                    insideThread
                                />
                            </ParentWrapper>
                        )}
                        <RepliesWrapper>
                            {isLoadingMessages && <Loader />}
                            {(messages || []).map(message => (
                                <AliveChatItem
                                    key={message.id}
                                    message={message}
                                    rid={rid}
                                    userId={userId}
                                    openImagePreview={noop}
                                    onEdit={this.onEdit}
                                    onShowReceipts={this.onShowReceipts}
                                    editActive={message.id === editMsgId && editActive}
                                    isReadOnly={readOnly}
                                    insideThread
                                />
                            ))}
                        </RepliesWrapper>
                    </ScrollArea>
                    {!readOnly ? (
                        <FooterWrapper>
                            <ChatFooter
                                inputRef={this.inputRef}
                                rid={rid}
                                message={inputValue}
                                previousMessage={inputPreviousValue}
                                editActive={editActive}
                                isMobile={isMobile}
                                onInputChange={this.onInputChange}
                                onSendMessage={this.sendMessage}
                                onInputKeyPress={this.onInputKeyPress}
                                uploadFiles={this.uploadFiles}
                            />
                        </FooterWrapper>
                    ) : null}
                </ColumnWrapper>
                {!!files[0] && (
                    <ChatFileUploadModal
                        file={files[0]}
                        isUploading={isUploading}
                        uploadFile={this.uploadFile}
                        closeFileForm={this.closeFileForm}
                    />
                )}
                <ReadByDialog
                    open={!!receiptsMessage}
                    onClose={this.onCloseReceipts}
                    messageId={receiptsMessage?.id}
                    createDate={receiptsMessage?.createDate}
                />
            </ViewWrapper>
        );
    }
}

export default connect(
    state => ({
        tmid: state.chat.thread.tmid,
        parentMessage: state.chat.thread.parentMessage,
        messages: state.chat.thread.messages,
        isLoadingMessages: state.chat.thread.isLoadingMessages,
        userId: state.user.profile.id,
        isMobile: state.global.isMobile,
    }),
    { sendThreadMessage, uploadThreadFile, updateChatMessage }
)(ThreadView);
